import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  Alert,
  Button,
  Drawer,
  Group,
  MultiSelect,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { filtersApi } from "@/api/client";
import type { Filter, FilterBulkChanges } from "@/api/types";
import { AMENITY_OPTIONS } from "./filterOptions";

type Action = "preserve" | "set" | "clear";
type Field = keyof FilterBulkChanges;
type DraftValue = number | string | boolean | string[] | null;
interface DraftEntry {
  action: Action;
  value: DraftValue;
}
type Draft = Record<Field, DraftEntry>;

const NUMBER_FIELDS: { field: Field; label: string; step?: number }[] = [
  { field: "min_rent", label: "Minimum rent (kr)" },
  { field: "max_rent", label: "Maximum rent (kr)" },
  { field: "min_rooms", label: "Minimum rooms", step: 0.5 },
  { field: "max_rooms", label: "Maximum rooms", step: 0.5 },
  { field: "min_size_sqm", label: "Minimum size (m²)" },
  { field: "max_size_sqm", label: "Maximum size (m²)" },
  { field: "max_metro_distance_m", label: "Maximum distance to metro (m)" },
  { field: "max_bus_distance_m", label: "Maximum distance to bus (m)" },
];

const TRANSPORT_NUMBER_FIELDS = NUMBER_FIELDS.filter(
  ({ field }) => field === "max_metro_distance_m" || field === "max_bus_distance_m"
);
const PROPERTY_NUMBER_FIELDS = NUMBER_FIELDS.filter(
  ({ field }) => !TRANSPORT_NUMBER_FIELDS.some((transportField) => transportField.field === field)
);

const BOOLEAN_FIELDS: { field: Field; label: string }[] = [
  { field: "furnished", label: "Furnished" },
  { field: "electricity_included", label: "Electricity included" },
  { field: "heating_included", label: "Heating included" },
  { field: "internet_included", label: "Internet included" },
  { field: "allow_bus_access", label: "Allow bus access" },
  { field: "pets_required", label: "Pets required" },
  { field: "until_further_notice", label: "Until further notice" },
  { field: "require_first_hand", label: "First hand" },
];

const ALL_FIELDS: Field[] = [
  ...NUMBER_FIELDS.map(({ field }) => field),
  "home_type",
  "amenities_required",
  ...BOOLEAN_FIELDS.map(({ field }) => field),
  "metro_lines_required",
];

const FIELD_LABELS: Record<Field, string> = Object.fromEntries([
  ...NUMBER_FIELDS.map(({ field, label }) => [field, label]),
  ["home_type", "Home type"],
  ["amenities_required", "Amenities"],
  ...BOOLEAN_FIELDS.map(({ field, label }) => [field, label]),
  ["metro_lines_required", "Metro lines"],
]) as Record<Field, string>;

interface PendingChange {
  field: Field;
  action: "set" | "clear";
  value: DraftValue;
}

function buildChanges(draft: Draft): {
  changes: FilterBulkChanges;
  summary: PendingChange[];
} {
  const changes: FilterBulkChanges = {};
  const summary: PendingChange[] = [];

  for (const field of ALL_FIELDS) {
    const entry = draft[field];
    if (entry.action === "clear") {
      Object.assign(changes, { [field]: null });
      summary.push({ field, action: "clear", value: null });
    } else if (entry.action === "set") {
      Object.assign(changes, { [field]: entry.value });
      summary.push({ field, action: "set", value: entry.value });
    }
  }

  return { changes, summary };
}

function formatValue(value: DraftValue): string {
  if (Array.isArray(value)) return value.length > 0 ? value.join(", ") : "None";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function createDraft(): Draft {
  return Object.fromEntries(
    ALL_FIELDS.map((field) => [field, { action: "preserve", value: null }])
  ) as Draft;
}

interface BulkEditDrawerProps {
  opened: boolean;
  filters: Filter[];
  onClose: () => void;
  onApplied: () => void;
}

export function BulkEditDrawer({ opened, filters, onClose, onApplied }: BulkEditDrawerProps) {
  const [draft, setDraft] = useState<Draft>(createDraft);
  const submissionPending = useRef(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ filterIds, changes }: { filterIds: number[]; changes: FilterBulkChanges }) =>
      filtersApi.bulkUpdate(filterIds, changes),
    onSuccess: async (_updatedFilters, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["filters"] });
      notifications.show({
        color: "green",
        title: "Filters updated",
        message: `${variables.filterIds.length} filter${variables.filterIds.length === 1 ? "" : "s"} updated.`,
      });
      setDraft(createDraft());
      onApplied();
    },
    onError: () => {
      notifications.show({
        color: "red",
        title: "Filters were not updated",
        message: "Please review your changes and try again.",
      });
    },
    onSettled: () => {
      submissionPending.current = false;
    },
  });

  const update = (field: Field, entry: Partial<DraftEntry>) =>
    setDraft((current) => ({
      ...current,
      [field]: { ...current[field], ...entry },
    }));

  const close = () => {
    setDraft(createDraft());
    mutation.reset();
    onClose();
  };

  const enabled = ALL_FIELDS.some((field) => draft[field].action !== "preserve");
  const invalidSet = ALL_FIELDS.some(
    (field) => draft[field].action === "set" && draft[field].value === null
  );

  const apply = () => {
    if (submissionPending.current || mutation.isPending) return;

    const { changes, summary } = buildChanges(draft);
    const filterIds = filters.map((filter) => filter.id);
    const filterCount = filterIds.length;

    modals.openConfirmModal({
      title: "Confirm bulk edit",
      centered: true,
      labels: { confirm: "Apply changes", cancel: "Cancel" },
      confirmProps: { color: "primary" },
      children: (
        <Stack gap="sm">
          <Text>
            Update {filterCount} selected filter{filterCount === 1 ? "" : "s"}?
          </Text>
          <Stack gap={4}>
            {summary.map(({ field, action, value }) => (
              <Text key={field} size="sm">
                <Text span fw={600}>
                  {action === "set" ? "Set" : "Clear"} {FIELD_LABELS[field]}
                </Text>
                {action === "set" ? `: ${formatValue(value)}` : ""}
              </Text>
            ))}
          </Stack>
          <Text size="sm" c="dimmed">
            Existing matches for these filters will be cleared and apartments will be re-evaluated.
          </Text>
        </Stack>
      ),
      onConfirm: () => {
        if (submissionPending.current) return;
        submissionPending.current = true;
        mutation.mutate({ filterIds, changes });
      },
    });
  };

  const actionSelect = (field: Field) => (
    <Select
      label="Action"
      aria-label={`${FIELD_LABELS[field]} action`}
      w={120}
      data={[
        { value: "preserve", label: "Preserve" },
        { value: "set", label: "Set" },
        { value: "clear", label: "Clear" },
      ]}
      value={draft[field].action}
      onChange={(value) => {
        const action = (value ?? "preserve") as Action;
        let nextValue = draft[field].value;
        if (action === "set" && nextValue === null) {
          if (field === "home_type") nextValue = "apartment";
          else if (field === "amenities_required" || field === "metro_lines_required")
            nextValue = [];
          else if (BOOLEAN_FIELDS.some((item) => item.field === field)) nextValue = false;
        }
        update(field, { action, value: nextValue });
      }}
      allowDeselect={false}
    />
  );

  return (
    <Drawer
      opened={opened}
      onClose={close}
      position="right"
      size="lg"
      title="Edit selected filters"
    >
      <Stack gap="md">
        <Text c="dimmed">
          {filters.length} selected: {filters.map((filter) => filter.name).join(", ")}
        </Text>
        <Text size="sm">Only criteria whose action is Set or Clear will be changed.</Text>

        <Title order={4}>Rent, rooms & size</Title>
        {PROPERTY_NUMBER_FIELDS.map(({ field, label, step }) => (
          <Paper key={field} withBorder p="sm" style={{ borderColor: "#0f3460" }}>
            <Group align="end" wrap="nowrap">
              {actionSelect(field)}
              <NumberInput
                label={label}
                step={step}
                min={0}
                flex={1}
                disabled={draft[field].action !== "set"}
                value={typeof draft[field].value === "number" ? draft[field].value : ""}
                onChange={(value) => update(field, { value: value === "" ? null : Number(value) })}
              />
            </Group>
          </Paper>
        ))}

        <Title order={4}>Property</Title>
        <Paper withBorder p="sm" style={{ borderColor: "#0f3460" }}>
          <Group align="end" wrap="nowrap">
            {actionSelect("home_type")}
            <Select
              label="Home type"
              flex={1}
              disabled={draft.home_type.action !== "set"}
              data={[
                { value: "apartment", label: "Apartment" },
                { value: "house", label: "House" },
                { value: "room", label: "Room" },
              ]}
              value={typeof draft.home_type.value === "string" ? draft.home_type.value : null}
              onChange={(value) => update("home_type", { value })}
            />
          </Group>
        </Paper>
        <Paper withBorder p="sm" style={{ borderColor: "#0f3460" }}>
          <Stack gap="xs">
            {actionSelect("amenities_required")}
            <MultiSelect
              label="Amenities"
              disabled={draft.amenities_required.action !== "set"}
              data={AMENITY_OPTIONS}
              value={
                Array.isArray(draft.amenities_required.value) ? draft.amenities_required.value : []
              }
              onChange={(value) => update("amenities_required", { value })}
            />
          </Stack>
        </Paper>

        <Title order={4}>Requirements</Title>
        {BOOLEAN_FIELDS.filter(({ field }) => field !== "allow_bus_access").map(
          ({ field, label }) => (
            <Paper key={field} withBorder p="sm" style={{ borderColor: "#0f3460" }}>
              <Group align="end" wrap="nowrap">
                {actionSelect(field)}
                <Select
                  label={label}
                  flex={1}
                  disabled={draft[field].action !== "set"}
                  data={[
                    { value: "true", label: "Yes" },
                    { value: "false", label: "No" },
                  ]}
                  value={
                    typeof draft[field].value === "boolean" ? String(draft[field].value) : null
                  }
                  onChange={(value) => update(field, { value: value === "true" })}
                />
              </Group>
            </Paper>
          )
        )}

        <Title order={4}>Transport</Title>
        {TRANSPORT_NUMBER_FIELDS.map(({ field, label }) => (
          <Paper key={field} withBorder p="sm" style={{ borderColor: "#0f3460" }}>
            <Group align="end" wrap="nowrap">
              {actionSelect(field)}
              <NumberInput
                label={label}
                description={
                  field === "max_bus_distance_m"
                    ? "Used as an alternative limit when bus access is allowed"
                    : "Maximum walking distance to an accepted metro stop"
                }
                min={0}
                flex={1}
                disabled={draft[field].action !== "set"}
                value={typeof draft[field].value === "number" ? draft[field].value : ""}
                onChange={(value) => update(field, { value: value === "" ? null : Number(value) })}
              />
            </Group>
          </Paper>
        ))}
        <Paper withBorder p="sm" style={{ borderColor: "#0f3460" }}>
          <Group align="end" wrap="nowrap">
            {actionSelect("allow_bus_access")}
            <Select
              label="Allow bus access"
              description="Accept bus proximity as an alternative to metro proximity"
              flex={1}
              disabled={draft.allow_bus_access.action !== "set"}
              data={[
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
              ]}
              value={
                typeof draft.allow_bus_access.value === "boolean"
                  ? String(draft.allow_bus_access.value)
                  : null
              }
              onChange={(value) => update("allow_bus_access", { value: value === "true" })}
            />
          </Group>
        </Paper>
        <Paper withBorder p="sm" style={{ borderColor: "#0f3460" }}>
          <Stack gap="xs">
            {actionSelect("metro_lines_required")}
            <MultiSelect
              label="Metro lines"
              disabled={draft.metro_lines_required.action !== "set"}
              data={[
                { value: "green", label: "Green" },
                { value: "red", label: "Red" },
                { value: "blue", label: "Blue" },
              ]}
              value={
                Array.isArray(draft.metro_lines_required.value)
                  ? draft.metro_lines_required.value
                  : []
              }
              onChange={(value) => update("metro_lines_required", { value })}
            />
          </Stack>
        </Paper>

        {mutation.isError && (
          <Alert color="red">Could not update the selected filters. Please try again.</Alert>
        )}
        <Group justify="flex-end" pb="md">
          <Button variant="default" onClick={close}>
            Cancel
          </Button>
          <Button
            disabled={!enabled || invalidSet || mutation.isPending}
            loading={mutation.isPending}
            onClick={apply}
          >
            Apply
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
